import * as React from 'react';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import Paper from '@base/Paper/Paper';
import imageHelper from '@utils/imageHelper';
import styled from 'styled-components';
import { breakpoints } from '@styles/index';
import Header from '@layout/HeaderTemplates/Base';
import Link from 'next/link';
import pushRoute from '@utils/pushRoute';
import { Title, Description, StyledButton, CardTitle } from './styles';
import Modal from './Modal';

const Subscription = styled.section`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  column-gap: 16px;
  @media (max-width: ${breakpoints.mobile}) {
    display: block;
  }
`;

const StyledTitle = styled(Title)`
  grid-column: 7/19;
  text-align: center;
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: 1/13;
    text-align: left;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 0;
  }
`;

const FeaturesTitle = styled(H3)`
  font-size: 20px;
  line-height: 120%;
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 18px;
  }
`;

const SubscriptionFeatures = styled(Paper)`
  margin-top: 36px;
  grid-column: 7/19;
  border: 1px solid #f0f4f7;
  position: relative;
  margin-bottom: 40px;
  overflow: hidden;
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: 13/25;
    margin-top: 0;
    padding: 24px 16px;
    margin-bottom: 16px;
  }
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 24px;
    padding: 16px 24px;
    overflow: hidden;
  }
`;

const FeatureList = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 24px;
  column-gap: 16px;
  @media (max-width: ${breakpoints.tablet}) {
    margin-top: 24px;
    grid-template-columns: repeat(1, 1fr);
    row-gap: 15px;
  }
`;

const StyledDescription = styled(Description)`
  font-size: 16px;
  line-height: 150%;
  font-weight: 400;
  margin-bottom: 0;
  margin-left: 16px;
  @media (max-width: ${breakpoints.tablet}) {
    font-size: 14px;
  }
`;

const Flex = styled.div`
  display: flex;
`;

const BackgroundImage = styled.img`
  position: absolute;
  right: 24px;
  cursor: pointer;
  user-drag: none;
  user-select: none;
  object-fit: cover;
  @media (min-width: ${breakpoints.tablet}) {
    top: 24px;
  }
  @media (max-width: ${breakpoints.tablet}) {
    right: -16px;
    bottom: -16px;
  }
`;

const ExpertButton = styled(StyledButton)`
  grid-column: 10/16;
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: 13/25;
    width: 100%;
  }
`;

const ExpertDescription = styled(Description)`
  grid-column: 7/19;
  text-align: center;
  margin-top: 16px;
  margin-bottom: 0;
  @media (max-width: ${breakpoints.tablet}) {
    grid-column: 13/25;
    width: 100%;
    margin-top: 8px;
  }
`;

const ClassesContainer = styled.div`
  @media (max-width: ${breakpoints.mobile}) {
    margin-top: 56px;
  }
`;

const ClassesTitle = styled(CardTitle)`
  @media (max-width: ${breakpoints.mobile}) {
    margin: 0;
    padding: 16px;
  }
`;

const ClassContainer = styled.div`
  @media (max-width: ${breakpoints.mobile}) {
    cursor: pointer;
    background: #ffffff;
    box-shadow: inset 0px -1px 0px #f0f4f7;
    height: 64px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const ClassImage = styled.img`
  cursor: pointer;
  user-drag: none;
  user-select: none;
  width: 40px;
  height: 40px;
  object-fit: cover;
`;

const ChevIcon = styled(ClassImage)`
  width: 24px;
  height: 24px;
`;

const ClassInfo = styled.div`
  margin-left: 16px;
`;

const ClassTitle = styled.h3`
  font-size: 14px;
  line-height: 150%;
  font-weight: 400;
  color: '#3C4852';
`;

const ClassSubTitle = styled.h3`
  font-size: 12px;
  line-height: 150%;
  font-weight: 400;
  color: #808080;
`;

const StyledModel = styled(Modal)`
  display: none;
  @media (max-width: ${breakpoints.mobile}) {
    display: block;
  }
`;

const RenderClass = ({ _class }) => (
  <Link href={`goal/cbse-class-6/${_class.goal_uid}/subscribe`}>
    <ClassContainer>
      <Flex>
        <ClassImage
          src={_class.image}
          alt="class info"
          with="40"
          height="40"
          loading="lazy"
        />
        <ClassInfo>
          <ClassTitle>{_class.title}</ClassTitle>
          <ClassSubTitle>{`${_class.count} courses`}</ClassSubTitle>
        </ClassInfo>
      </Flex>
      <ChevIcon
        src={imageHelper.getImage('k12/chevron-right.png')}
        alt="chevron"
        width="24"
        height="24"
        loading="lazy"
      />
    </ClassContainer>
  </Link>
);

RenderClass.propTypes = {
  _class: PropTypes.objectOf(PropTypes.any).isRequired
};

function SubscriptionInfo({ data, classes }) {
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const {
    subscription: { features, title, subtitle, bgImage },
    subscriptionTitle,
    subscriptionSubtitle,
    buttonText
  } = data;
  const pushToExplore = () => {
    return pushRoute('/explore');
  };
  return (
    <Subscription>
      <StyledTitle>{subscriptionTitle}</StyledTitle>
      <SubscriptionFeatures>
        <FeaturesTitle>{title}</FeaturesTitle>
        <FeaturesTitle>{subtitle}</FeaturesTitle>
        <BackgroundImage
          src={bgImage}
          width="124"
          height="124"
          alt="feature"
          loading="lazy"
        />
        <FeatureList>
          {features.map((_feature) => (
            <Flex key={_feature.id}>
              <img
                src={_feature.image}
                alt="checked"
                width="24"
                height="24"
                loading="lazy"
              />
              <StyledDescription>{_feature.title}</StyledDescription>
            </Flex>
          ))}
        </FeatureList>
      </SubscriptionFeatures>
      <ExpertButton onClick={pushToExplore} $hideSm>
        {buttonText}
      </ExpertButton>
      <ExpertButton onClick={handleOpen} $hideLg $hideMd>
        {buttonText}
      </ExpertButton>
      <ExpertDescription>{subscriptionSubtitle}</ExpertDescription>
      <StyledModel open={open} handleClose={handleClose}>
        <Header handleClose={handleClose} showCloseButton />
        <ClassesContainer>
          <ClassesTitle>Choose your class</ClassesTitle>
          {classes.map((_class) => (
            <RenderClass _class={_class} key={_class.id} />
          ))}
        </ClassesContainer>
      </StyledModel>
    </Subscription>
  );
}

export default SubscriptionInfo;

SubscriptionInfo.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  // scrollToForm: PropTypes.func,
  classes: PropTypes.arrayOf(PropTypes.any).isRequired
};

SubscriptionInfo.defaultProps = {
  // scrollToForm: () => {}
};
